"use client";

// Coffre de BIENVENUE, proposé et non plus imposé.
//
// Il s'ouvrait en plein écran à l'arrivée sur la première formation, avant
// même la première vidéo : un cadeau Reps (clés, bouclier, fragments) offert à
// quelqu'un qui venait d'arriver pour apprendre autre chose et ne savait pas
// encore à quoi tout cela sert. Il attend maintenant dans un bloc, avec ce
// qu'il contient et le temps qu'il reste : le serveur ne le garde que 24 heures
// après l'inscription (RepsKeyService::WELCOME_WINDOW_HOURS), passé ce délai
// il est perdu.
//
// C'est un coffre TUTORIEL, le premier que la personne ouvre : avant, BeBot
// dit ce qu'est un coffre et comment on en gagne ; à l'ouverture, chaque
// récompense est révélée avec une phrase sur ce à quoi elle sert.
//
// Deux présentations : « card » dans la colonne de droite du tableau de bord,
// « compact » sur une page de formation (une ligne, sans rien recouvrir).
import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import ChestOpeningModalV4 from "@/components/Quests/ChestOpeningModalV4";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function WelcomeChestCard({ variant = "card", className = "" }) {
    const { data: session, status } = useSession();
    const t = useTranslations();
    const [chest, setChest] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    // null (bloc seul) -> "intro" (ce qu'est un coffre) -> "chest" (ouverture,
    // chaque récompense révélée avec ce à quoi elle sert)
    const [phase, setPhase] = useState(null);
    const [claimed, setClaimed] = useState(false);
    const opening = phase !== null;
    const setOpening = () => setPhase("intro");

    const isOnboarded = Boolean(session?.user?.isOnboarded);

    useEffect(() => {
        if (status !== "authenticated" || !isOnboarded) return;
        let cancelled = false;
        fetch("/api/reps/welcome", { method: "POST" })
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (cancelled || !data?.showWelcome || !data.expiresAt) return;
                const expiresAt = new Date(data.expiresAt).getTime();
                if (Number.isNaN(expiresAt)) return;
                setNow(Date.now());
                setChest({
                    // Replis alignés sur RepsKeyService (3/1/80) : le serveur
                    // est prioritaire, ils ne parlent qu'en son absence.
                    // keys peut valoir 0 : un compte qui n'utilise pas Reps
                    // reçoit des fragments à la place.
                    keys: data.chest?.keys ?? 3,
                    shields: data.chest?.shields ?? 1,
                    fragments: data.chest?.fragments ?? 80,
                    expiresAt,
                });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [status, isOnboarded]);

    // Objet STABLE : la modale du coffre repart de zéro si ses récompenses
    // changent d'identité, et le compte à rebours fait re-rendre ce composant
    // toutes les trente secondes.
    const chestRewards = useMemo(
        () =>
            chest
                ? {
                      rarity: "legendary",
                      drops: [
                          {
                              rarity: "legendary",
                              rewards: [
                                  { type: "bonus_key", amount: chest.keys },
                                  { type: "shield", amount: chest.shields },
                                  { type: "fragment", amount: chest.fragments },
                              ].filter((reward) => reward.amount > 0),
                          },
                      ],
                  }
                : null,
        [chest],
    );

    // Le compte à rebours s'affiche à la minute : un passage toutes les
    // trente secondes suffit.
    useEffect(() => {
        if (!chest) return;
        const timer = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(timer);
    }, [chest]);

    // Fermeture du coffre : c'est elle qui octroie les récompenses côté
    // serveur (idempotent), puis les compteurs de l'en-tête se rafraîchissent.
    const claim = () => {
        setClaimed(true);
        setPhase(null);
        fetch("/api/reps/welcome-ack", { method: "POST" })
            .then(() => window.dispatchEvent(new Event("reps:keys-changed")))
            .catch(() => {});
    };

    if (!chest) return null;

    const msLeft = chest.expiresAt - now;
    if (msLeft <= 0 && !opening) return null;
    if (claimed && !opening) return null;

    const totalMinutes = Math.max(1, Math.floor(msLeft / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const timeLeft =
        hours >= 1
            ? t("reps_welcome_card_time_hours", {
                  hours,
                  minutes: String(minutes).padStart(2, "0"),
              })
            : t("reps_welcome_card_time_minutes", { minutes });

    // Une phrase par récompense, montrée au moment où le coffre la révèle
    const rewardHints = {
        bonus_key: t("reps_welcome_tuto_keys_text"),
        shield: t("reps_welcome_tuto_shield_text"),
        fragment: t("reps_welcome_tuto_fragments_text"),
    };

    const modal = (
        <>
            {/* 1. Ce qu'est un coffre, avant de l'ouvrir */}
            <BottomSheetModal
                isOpen={phase === "intro"}
                onClose={() => setPhase(null)}
            >
                <div className="px-7 pb-8 pt-7 text-center">
                    <Image
                        src="/images/bebot/illustrations/bebot-chest.png"
                        alt="BeBot"
                        width={220}
                        height={304}
                        className="mx-auto h-40 w-auto"
                    />
                    <h2 className="mb-0 mt-5 text-[22px] font-bold leading-tight text-gray-900 dark:text-white">
                        {t("reps_welcome_tuto_intro_title")}
                    </h2>
                    <p className="mb-0 mt-3 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                        {t("reps_welcome_tuto_intro_text")}
                    </p>
                    <button
                        type="button"
                        onClick={() => setPhase("chest")}
                        className="btn-3d lip-orange mt-7 w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                    >
                        {t("reps_welcome_chest_open")}
                    </button>
                </div>
            </BottomSheetModal>

            {/* 2. L'ouverture */}
            {phase === "chest" && (
                <ChestOpeningModalV4
                    isOpen={true}
                    onClose={claim}
                    rewards={chestRewards}
                    rewardHints={rewardHints}
                    chestType="normal"
                />
            )}
        </>
    );

    if (claimed) return modal;

    if (variant === "compact") {
        return (
            <>
                <div
                    className={`flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-borderDark dark:bg-bgDark-800 ${className}`}
                >
                    <Image
                        src="/images/bebot/illustrations/bebot-chest.png"
                        alt="BeBot"
                        width={88}
                        height={88}
                        className="h-10 w-auto shrink-0"
                    />
                    <p className="mb-0 min-w-0 flex-1 text-sm text-gray-600 dark:text-grayDark-300">
                        {t("reps_welcome_card_compact", { time: timeLeft })}
                    </p>
                    <button
                        type="button"
                        onClick={() => setOpening()}
                        className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                    >
                        {t("reps_welcome_card_compact_open")}
                    </button>
                </div>
                {modal}
            </>
        );
    }

    // Même gabarit que la carte d'essai de la colonne de droite : BeBot en
    // coin, une pastille, un titre, une ligne, un bouton. Le contenu du coffre
    // n'est pas annoncé, on le découvre en l'ouvrant.
    return (
        <>
            <div
                className={`relative overflow-hidden rounded-2xl border border-orange-100 bg-gradient-to-br from-white to-orange-50 p-5 shadow-sm dark:border-borderDark dark:from-bgDark-840 dark:to-bgDark-840 ${className}`}
            >
                <Image
                    src="/images/bebot/illustrations/bebot-chest.png"
                    alt="BeBot"
                    width={150}
                    height={208}
                    className="pointer-events-none absolute right-4 top-4 h-[104px] w-auto select-none"
                />
                <div className="relative min-h-[104px] pr-24">
                    <span className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">
                        <SolarIcon
                            name="clock-circle-linear"
                            className="text-xs"
                        />
                        {timeLeft}
                    </span>
                    <h3 className="mt-2 text-base font-bold leading-snug text-gray-900 dark:text-white">
                        {t("reps_welcome_card_title")}
                    </h3>
                    <p className="mb-0 mt-1 text-sm leading-snug text-gray-600 dark:text-grayDark-300">
                        {t("reps_welcome_card_intro")}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setOpening()}
                    className="btn-3d lip-orange relative mt-4 flex w-full items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                >
                    {t("reps_welcome_chest_open")}
                </button>
            </div>
            {modal}
        </>
    );
}
